import { useState, type FormEvent, type ReactNode } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  ExternalLink,
  GitPullRequest,
  Loader2,
} from "lucide-react";
import type { ProjectDatabase } from "@mycompbox/shared";
import {
  useCreateCloudSqlDatabase,
  useDisconnectDatabase,
  useProjectDatabase,
  useSaveDatabaseConnection,
} from "@/hooks/queries/use-project-database";
import { useProjectCloud, useProjectStack } from "@/hooks/queries/use-cloud";
import { showToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SUPABASE_NEW_PROJECT_URL = "https://supabase.com/dashboard/new";

const ENGINE: Record<ProjectDatabase["engine"], string> = { postgres: "Postgres", mysql: "MySQL" };

type Open = "cloudsql" | "paste" | "disconnect" | null;

/**
 * The project's database (ENG-317): create one in Cloud SQL, paste a connection
 * string, or see what's connected. The connection string is sent once and is
 * never shown, stored in the page, or returned by the API.
 */
export function ProjectDatabaseCard({ teamId, canEdit }: { teamId: string; canEdit: boolean }) {
  const { data: view, isLoading } = useProjectDatabase(teamId);
  const { data: projectCloud } = useProjectCloud(teamId);
  const { data: stack } = useProjectStack(teamId);
  const [open, setOpen] = useState<Open>(null);

  if (isLoading || !view) return null;
  const database = view.data;
  const onGoogleCloud = projectCloud?.cloud?.provider === "gcp";

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium">Database</h3>
        <p className="text-sm text-muted-foreground">
          The database this project's app uses. CompBox saves its connection string as the
          repository's <code className="text-xs">DATABASE_URL</code> secret and never shows it
          again.
        </p>
      </div>

      {database ? (
        <ConnectedDatabase
          database={database}
          prUrl={stack?.prUrl ?? null}
          canEdit={canEdit}
          onReplace={() => {
            setOpen("paste");
          }}
          onDisconnect={() => {
            setOpen("disconnect");
          }}
        />
      ) : canEdit ? (
        <div className="space-y-3">
          {onGoogleCloud ? (
            <Option
              title="Create one in Cloud SQL"
              body={`CompBox adds a Postgres database to the stack's pull request. About $${String(view.estimatedMonthlyUsd)} a month, billed by Google Cloud once the pull request is merged.`}
            >
              <Button
                size="sm"
                onClick={() => {
                  setOpen("cloudsql");
                }}
              >
                Create database
              </Button>
            </Option>
          ) : (
            <p className="text-sm text-muted-foreground">
              One-click databases need the project to deploy to Google Cloud. You can still paste a
              connection string.
            </p>
          )}
          <Option
            title="Paste a connection string"
            body="From a database you already have, e.g. Neon or Supabase."
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen("paste");
              }}
            >
              Paste connection string
            </Button>
          </Option>
          <Option
            title="Make a free Supabase database"
            body="Create one on Supabase, then paste its connection string here."
          >
            <Button asChild size="sm" variant="outline">
              <a href={SUPABASE_NEW_PROJECT_URL} target="_blank" rel="noreferrer">
                Open Supabase
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
          </Option>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No database connected. A project admin can create or connect one.
        </p>
      )}

      {canEdit ? (
        <>
          <CloudSqlDialog
            teamId={teamId}
            open={open === "cloudsql"}
            estimatedMonthlyUsd={view.estimatedMonthlyUsd}
            onClose={() => {
              setOpen(null);
            }}
          />
          <PasteDialog
            teamId={teamId}
            open={open === "paste"}
            replacing={!!database}
            onClose={() => {
              setOpen(null);
            }}
          />
          <DisconnectDialog
            teamId={teamId}
            open={open === "disconnect"}
            onClose={() => {
              setOpen(null);
            }}
          />
        </>
      ) : null}
    </div>
  );
}

function Option({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function ConnectedDatabase({
  database,
  prUrl,
  canEdit,
  onReplace,
  onDisconnect,
}: {
  database: ProjectDatabase;
  prUrl: string | null;
  canEdit: boolean;
  onReplace: () => void;
  onDisconnect: () => void;
}) {
  const where = database.providerLabel ?? database.host;
  const label = `${ENGINE[database.engine]}${where ? ` on ${where}` : ""}`;
  // A pasted database can always be replaced; a Cloud SQL one is managed by the stack.
  const canReplace = canEdit && database.source === "pasted";

  return (
    <div className="space-y-2 rounded-lg border border-white/[0.08] p-3 text-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium">{label}</span>
          <StatusBadge status={database.status} />
        </div>
        {canEdit ? (
          <div className="flex shrink-0 gap-2">
            {canReplace ? (
              <Button size="sm" variant="outline" onClick={onReplace}>
                Replace
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={onDisconnect}>
              Disconnect
            </Button>
          </div>
        ) : null}
      </div>
      {database.environments.length > 0 ? (
        <p className="text-xs text-muted-foreground">Used by: {database.environments.join(", ")}</p>
      ) : null}
      {database.status === "pending" && database.source === "cloudsql" ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Merge the stack's pull request to create it. Nothing is billed until then.
          </p>
          {prUrl ? (
            <Button asChild size="sm" variant="outline">
              <a href={prUrl} target="_blank" rel="noreferrer">
                <GitPullRequest className="mr-1 h-3.5 w-3.5" />
                Approve in GitHub
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
          ) : null}
        </div>
      ) : null}
      {database.status === "error" && database.lastError ? (
        <p className="text-xs text-destructive">{database.lastError}</p>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }: { status: ProjectDatabase["status"] }) {
  if (status === "connected") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-500">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Connected
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" />
        Needs attention
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5" />
      Waiting for the pull request
    </span>
  );
}

function CloudSqlDialog({
  teamId,
  open,
  estimatedMonthlyUsd,
  onClose,
}: {
  teamId: string;
  open: boolean;
  estimatedMonthlyUsd: number;
  onClose: () => void;
}) {
  const create = useCreateCloudSqlDatabase(teamId);
  const [confirmed, setConfirmed] = useState(false);

  const close = () => {
    setConfirmed(false);
    create.reset();
    onClose();
  };

  const submit = () => {
    create.mutate(undefined, {
      onSuccess: () => {
        showToast("Added a Cloud SQL database to the stack's pull request.");
        close();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a Cloud SQL database?</DialogTitle>
          <DialogDescription>
            CompBox adds a Postgres database to the stack's pull request, one per environment.
            Google Cloud bills about ${estimatedMonthlyUsd} a month once the pull request is merged.
            This is an estimate, not a quote.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmed}
            onChange={(e) => {
              setConfirmed(e.target.checked);
            }}
          />
          <span>I understand this costs about ${estimatedMonthlyUsd} a month.</span>
        </label>
        {create.error ? (
          <p role="alert" className="text-sm text-destructive">
            {create.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!confirmed || create.isPending}>
            {create.isPending ? "Creating…" : "Create database"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasteDialog({
  teamId,
  open,
  replacing,
  onClose,
}: {
  teamId: string;
  open: boolean;
  replacing: boolean;
  onClose: () => void;
}) {
  const save = useSaveDatabaseConnection(teamId);
  const [value, setValue] = useState("");

  // Clear the secret from state on every way out: cancel, close, success.
  const close = () => {
    setValue("");
    save.reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const connectionString = value.trim();
    if (!connectionString) return;
    save.mutate(
      { connectionString },
      {
        onSuccess: () => {
          showToast(replacing ? "Database connection replaced." : "Database connected.");
          close();
        },
        // Don't keep a rejected value around either.
        onError: () => {
          setValue("");
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !save.isPending) close();
      }}
    >
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {replacing ? "Replace the connection string" : "Paste a connection string"}
            </DialogTitle>
            <DialogDescription>
              Saved as the repository's DATABASE_URL secret for every environment. CompBox doesn't
              keep it and won't show it again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="database-connection-string">Connection string</Label>
            <Input
              id="database-connection-string"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="postgres://user:password@host:5432/db"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
            />
          </div>
          {save.error ? (
            <p role="alert" className="text-sm text-destructive">
              {save.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!value.trim() || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DisconnectDialog({
  teamId,
  open,
  onClose,
}: {
  teamId: string;
  open: boolean;
  onClose: () => void;
}) {
  const disconnect = useDisconnectDatabase(teamId);

  const close = () => {
    disconnect.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !disconnect.isPending) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disconnect the database?</DialogTitle>
          <DialogDescription>
            CompBox removes DATABASE_URL from every environment. The database itself, and its data,
            are not deleted.
          </DialogDescription>
        </DialogHeader>
        {disconnect.error ? (
          <p role="alert" className="text-sm text-destructive">
            {disconnect.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={disconnect.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={disconnect.isPending}
            onClick={() => {
              disconnect.mutate(undefined, {
                onSuccess: () => {
                  showToast("Database disconnected.");
                  close();
                },
              });
            }}
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
